import { isContactable } from './prospects';

// Línea obligatoria en todo mensaje: la base de datos rechaza uno que no diga "responde BAJA".
export const OPT_OUT_LINE = 'Si no quieres recibir más mensajes, responde BAJA.';

// Las versiones del mensaje (separadas por una línea "---") suman como máximo MAX_TEMPLATE_LENGTH; cada una, MAX_VARIANT_LENGTH.
export const MAX_TEMPLATE_LENGTH = 2400;
export const MAX_VARIANT_LENGTH = 700;
export const MAX_VARIANTS = 5;
export const MAX_MESSAGE_LENGTH = 1000;
export const MAX_BATCH_SIZE = 50;
export const MAX_HOURLY_CAP = 15;
export const VARIANT_SEPARATOR = '---';

// Mensajes de apertura: suenan a una persona escribiendo desde su celular (sin "asistente virtual", sin lista de beneficios),
// con un dato real del negocio y UNA pregunta fácil de contestar. Venden después, ya conversando.
// Varias versiones para que no salgan todos idénticos (se ve menos como envío masivo y baja el riesgo de bloqueo).
export const DEFAULT_TEMPLATES = [
  'Hola 👋 ¿hablo con {{nombre}}? Te escribo de {{empresa}}. {{Elogio}} y me dio curiosidad algo: cuando un cliente les escribe por WhatsApp y están ocupados, ¿alcanzan a contestarle a todos? 🙂',
  'Hola, ¿cómo vas? 😊 Soy Keyla, de {{empresa}}. Andaba mirando negocios de {{rubro}} y {{elogio}}. Una duda rápida: ¿quién les contesta el WhatsApp cuando están con un cliente?',
  'Hola 👋 Soy Keyla de {{empresa}}. {{Elogio}}, felicitaciones 👏 Te escribo porque ayudamos a negocios como {{nombre}} a no perder clientes por no alcanzar a contestar el WhatsApp. ¿Te cuento cómo en un minutito?'
];
export const DEFAULT_TEMPLATE = DEFAULT_TEMPLATES.join(`\n${VARIANT_SEPARATOR}\n`);

export const TEMPLATE_VARIABLES = [
  ['{{nombre}}', 'nombre del negocio al que le escribes'],
  ['{{rubro}}', 'su rubro (barbería, clínica…)'],
  ['{{ciudad}}', 'su ciudad'],
  ['{{empresa}}', 'el nombre de tu negocio'],
  ['{{Elogio}}', 'frase con un dato real de Google (su calificación o reseñas). {{elogio}} la deja en minúscula']
];

export const DAYS = [
  [1, 'Lun'], [2, 'Mar'], [3, 'Mié'], [4, 'Jue'], [5, 'Vie'], [6, 'Sáb'], [7, 'Dom']
];

// Mismos valores y topes que las restricciones de prospect_settings en la base de datos.
export const DEFAULT_SETTINGS = {
  message_template: DEFAULT_TEMPLATE,
  daily_cap: 20,
  hourly_cap: 8,
  window_start: '09:00',
  window_end: '17:00',
  send_days: [1, 2, 3, 4, 5],
  min_gap_seconds: 45,
  max_gap_seconds: 120,
  sending_enabled: false
};

export const timeHM = (value) => String(value ?? '').slice(0, 5);

export const splitVariants = (template) =>
  String(template ?? '').split(new RegExp(`^[ \\t]*${VARIANT_SEPARATOR}[ \\t]*$`, 'm')).map(v => v.trim()).filter(Boolean);

// Cada prospecto recibe siempre la misma versión (se elige por su id): lo que se ve al aprobar es lo que sale.
export function pickVariant(variants, key) {
  if (!variants.length) return '';
  let hash = 5381;
  String(key ?? '').split('').forEach((ch) => { hash = ((hash << 5) + hash + ch.charCodeAt(0)) | 0; });
  return variants[Math.abs(hash) % variants.length];
}

// Frase natural con un dato real de Google. Solo presume de la calificación si de verdad es buena.
export function elogioFor(prospect) {
  const rating = Number(prospect?.rating);
  const reviews = Number(prospect?.reviews_count);
  if (rating >= 4.3 && reviews >= 20) return `vi que tienen muy buenas reseñas en Google (⭐ ${rating} con ${reviews} reseñas)`;
  if (rating >= 4.3) return `vi que tienen muy buena calificación en Google (⭐ ${rating})`;
  return 'vi su negocio en Google Maps';
}

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// Mensaje final de un prospecto: versión elegida, variables sustituidas y la línea de baja siempre al final.
export function renderMessage(template, prospect, businessName) {
  const vars = {
    nombre: (prospect?.name || '').trim() || 'tu negocio',
    rubro: (prospect?.category || '').trim() || 'tu rubro',
    ciudad: (prospect?.city || '').trim() || 'tu ciudad',
    empresa: (businessName || '').trim() || 'nuestro equipo',
    elogio: elogioFor(prospect)
  };
  const chosen = pickVariant(splitVariants(template), prospect?.id ?? prospect?.name);
  let text = chosen.replace(/\{\{\s*(nombre|rubro|ciudad|empresa|elogio)\s*\}\}/gi, (raw, key) => {
    const value = vars[key.toLowerCase()];
    return key === 'Elogio' ? capitalize(value) : value;
  });
  text = text.replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
  if (!/responde\s+baja/i.test(text)) text = [text, OPT_OUT_LINE].filter(Boolean).join('\n\n');
  if (text.length > MAX_MESSAGE_LENGTH) return { ok: false, text, error: 'El mensaje es demasiado largo (máximo 1000 caracteres con la línea de baja).' };
  if (text.length < 30) return { ok: false, text, error: 'El mensaje es demasiado corto.' };
  return { ok: true, text, error: null };
}

// Todas las versiones renderizadas para un mismo prospecto (vista previa en los ajustes).
export const previewVariants = (template, prospect, businessName) =>
  splitVariants(template).map(v => renderMessage(v, prospect, businessName));

export function validateSettings(s) {
  const cap = Number(s.daily_cap);
  const min = Number(s.min_gap_seconds);
  const max = Number(s.max_gap_seconds);
  const days = Array.isArray(s.send_days) ? s.send_days : [];
  if (!String(s.message_template ?? '').trim()) return 'Escribe el mensaje que se enviará.';
  if (String(s.message_template).length > MAX_TEMPLATE_LENGTH) return `El mensaje no puede pasar de ${MAX_TEMPLATE_LENGTH} caracteres.`;
  const variants = splitVariants(s.message_template);
  if (variants.length > MAX_VARIANTS) return `Puedes tener hasta ${MAX_VARIANTS} versiones del mensaje.`;
  if (variants.some(v => v.length > MAX_VARIANT_LENGTH)) return `Cada versión del mensaje puede tener hasta ${MAX_VARIANT_LENGTH} caracteres.`;
  if (!Number.isInteger(cap) || cap < 1 || cap > 50) return 'El tope diario debe estar entre 1 y 50 mensajes.';
  const hourly = Number(s.hourly_cap);
  if (!Number.isInteger(hourly) || hourly < 1 || hourly > MAX_HOURLY_CAP) return `El tope por hora debe estar entre 1 y ${MAX_HOURLY_CAP} mensajes.`;
  if (!Number.isInteger(min) || min < 20) return 'La pausa mínima entre mensajes es de 20 segundos.';
  if (!Number.isInteger(max) || max < min) return 'La pausa máxima no puede ser menor que la mínima.';
  if (max > 900) return 'La pausa máxima es de 900 segundos (15 minutos).';
  if (!timeHM(s.window_start) || !timeHM(s.window_end) || timeHM(s.window_start) >= timeHM(s.window_end)) return 'La hora de inicio debe ser anterior a la hora de fin.';
  if (days.length === 0 || days.some(d => !Number.isInteger(d) || d < 1 || d > 7)) return 'Elige al menos un día de envío.';
  return null;
}

// Solo columnas que el navegador tiene permiso de escribir (el resto lo escribe n8n).
export const settingsPayload = (businessId, s) => ({
  business_id: String(businessId),
  message_template: String(s.message_template).trim(),
  daily_cap: Number(s.daily_cap),
  hourly_cap: Number(s.hourly_cap),
  window_start: timeHM(s.window_start),
  window_end: timeHM(s.window_end),
  send_days: [...s.send_days].map(Number).sort((a, b) => a - b),
  min_gap_seconds: Number(s.min_gap_seconds),
  max_gap_seconds: Number(s.max_gap_seconds),
  sending_enabled: Boolean(s.sending_enabled),
  updated_at: new Date().toISOString()
});

const OPEN_STATUSES = ['aprobado', 'enviando', 'enviado'];

// Prospectos que ya tienen un mensaje aprobado, en curso o enviado (no se les puede aprobar otro).
export const queuedProspectIds = (messages) =>
  new Set((messages || []).filter(m => m.prospect_id && OPEN_STATUSES.includes(m.status)).map(m => m.prospect_id));

export const eligibleForBatch = (prospects, keys, queued) =>
  (prospects || []).filter(p => isContactable(p, keys) && p.phone && !queued.has(p.id));

export function buildBatch(prospects, template, businessName) {
  return (prospects || []).slice(0, MAX_BATCH_SIZE).map(p => ({ prospect: p, ...renderMessage(template, p, businessName) }));
}

export function queueSummary(messages) {
  const out = { aprobado: 0, enviando: 0, enviado: 0, error: 0, cancelado: 0, omitido: 0 };
  (messages || []).forEach(m => { if (m.status in out) out[m.status] += 1; });
  return out;
}

const localDay = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
export const sentToday = (messages, now = new Date()) =>
  (messages || []).filter(m => m.status === 'enviado' && m.sent_at && localDay(new Date(m.sent_at)) === localDay(now)).length;

// Calentamiento: un número que empieza a enviar a desconocidos no debe mandar el tope completo desde el primer día.
// El tope real es el MENOR entre el tope que configuraste y el de calentamiento según los días desde el primer envío.
// Son los mismos tramos que usa el flujo de n8n (Flujo_Prospeccion_Envio.json); una prueba comprueba que coinciden.
export const WARMUP_STEPS = [
  { fromDay: 0, cap: 10 }, { fromDay: 3, cap: 15 }, { fromDay: 7, cap: 25 }, { fromDay: 14, cap: 50 }
];
export const warmupCap = (daysSinceFirstSend) => {
  const days = Number.isFinite(daysSinceFirstSend) && daysSinceFirstSend > 0 ? daysSinceFirstSend : 0;
  return [...WARMUP_STEPS].reverse().find(step => days >= step.fromDay).cap;
};
export const effectiveDailyCap = (dailyCap, daysSinceFirstSend) => Math.min(Number(dailyCap) || 0, warmupCap(daysSinceFirstSend));

// Días (calendario local) desde el primer mensaje enviado; 0 si todavía no se envió ninguno.
export function daysSinceFirstSend(messages, now = new Date()) {
  const times = (messages || []).filter(m => m.status === 'enviado' && m.sent_at).map(m => new Date(m.sent_at).getTime());
  if (!times.length) return 0;
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.max(0, Math.round((startOfDay(now) - startOfDay(new Date(Math.min(...times)))) / 86400000));
}

// Freno automático (lo aplica n8n): se pausan los envíos si piden la baja 6 personas, o 3 o más que sean el 5 % o más de lo enviado
// en los últimos 7 días (se cuenta desde la última vez que cambiaste los ajustes, para que reactivar no lo vuelva a frenar de inmediato).
export const BAJAS_BRAKE = { absolute: 6, minimum: 3, percentOfSent: 5 };

// Cuántos días de envío hacen falta para vaciar la cola con este tope diario (orientativo).
export const daysToFinish = (pending, dailyCap) => (pending > 0 && dailyCap > 0 ? Math.ceil(pending / dailyCap) : 0);

export const STATUS_LABEL = {
  aprobado: 'En cola', enviando: 'Enviando', enviado: 'Enviado', error: 'Con error', cancelado: 'Cancelado', omitido: 'Omitido'
};
